import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Spacer, Divider, Select, Empty, Flex } from '@oliasoft-open-source/react-ui-library';
import translations from 'src/internationalisation/translation-map.json';
import { probabilities } from 'src/enums/simulations';
import { SummaryChart } from 'views/campaigns/summary/summary-chart';
import { Kpi } from 'views/campaigns/kpi/kpi';
import { SimulationChartType } from 'src/enums/campaigns';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { SummaryTable } from 'views/campaigns/summary/summary-table';
import { selectUnitByQuantity } from 'store/entities/unit-templates/selectors';
import { QuantitiesTypes } from 'src/enums/unit-templates';

const Summary = ({
  activeCampaign,
  kpiModalUpdated,
  primaryCurrency,
  activeCampaignTotal,
  isRecalculating,
  updateIsRecalculating,
  isGeneratingEstimates,
  setDataEntity,
  dataEntity,
  probability,
  setProbability,
  depthUnit,
}) => {
  const { t } = useTranslation();
  const [chartType, setChartType] = useState(SimulationChartType.TOTAL_DAYS);
  const [showTable, setShowTable] = useState(false);
  const probabilityOptions = Object.keys(probabilities).map((key) => ({
    label: key,
    value: probabilities[key],
  }));
  const chartTypeOptions = [
    {
      label: t(dataEntity === 0 ? translations.campaigns_totalDays : translations.campaigns_totalCost),
      value: SimulationChartType.TOTAL_DAYS,
    },
    {
      label: t(depthUnit === 'm' ? translations.campaigns_metersPerDay : translations.campaigns_feetPerDay),
      value: SimulationChartType.METERS_PER_DAY,
    },
    {
      label: t(translations.simulations_cumulativeProbability),
      value: SimulationChartType.CUMULATIVE_PROBABILITY,
    },
  ];

  const controlsPortalId = `campaing-chart-controls-${chartType}`;

  return (
    <>
      {activeCampaign?.estimates?.length === 0 ? (
        <Flex>
          <Spacer height="300px" />
          <Empty height="100%" width="100%" text={t(translations.campaigns_noEstimatesSelected)} />
        </Flex>
      ) : (
        !isRecalculating && (
          <>
            <Spacer />
            <Flex>
              <Kpi kpiModalUpdated={kpiModalUpdated} campaign={activeCampaign} />
            </Flex>
            <Divider />
            <Flex justifyContent="space-between">
              <Flex>
                <Select
                  name="chartType"
                  width="auto"
                  options={chartTypeOptions}
                  small
                  testId="campaigns-select-chart-type"
                  onChange={(e) => {
                    const { value } = e.target;
                    if (value === SimulationChartType.METERS_PER_DAY) {
                      setDataEntity(0);
                    }
                    setChartType(value);
                  }}
                  value={chartType}
                />
                <Spacer width="var(--padding)" />
                <ButtonGroup
                  testId="campaigns-button-group-view-mode"
                  items={[
                    {
                      testId: 'chart',
                      key: 0,
                      icon: 'chart',
                    },
                    {
                      testId: 'table',
                      key: 1,
                      icon: 'table',
                    },
                  ]}
                  small
                  value={+showTable}
                  onSelected={(key) => {
                    setShowTable(!!key);
                  }}
                />
                <Spacer width="var(--padding)" />
                {!showTable && chartType !== SimulationChartType.CUMULATIVE_PROBABILITY && (
                  <Select
                    name="probability"
                    width="auto"
                    options={probabilityOptions}
                    small
                    testId="campaigns-select-probability"
                    onChange={(e) => setProbability(e.target.value)}
                    value={probability}
                  />
                )}
                <Spacer width="var(--padding)" />
                <ButtonGroup
                  testId="campaigns-button-group-time-cost-type"
                  items={[
                    {
                      key: 0,
                      label: t(translations.time),
                      value: 'time',
                      testId: 'time',
                    },
                    {
                      key: 1,
                      label: t(translations.cost),
                      value: 'cost',
                      testId: 'cost',
                      disabled: chartType === SimulationChartType.METERS_PER_DAY,
                    },
                  ]}
                  small
                  onSelected={(key) => {
                    setDataEntity(key);
                  }}
                  value={dataEntity}
                />
              </Flex>
              <Flex>
                <div id={controlsPortalId} />
              </Flex>
            </Flex>
            <Spacer />
          </>
        )
      )}
      {!showTable && (
        <SummaryChart
          campaign={activeCampaign}
          probability={probability}
          dataEntity={dataEntity}
          primaryCurrency={primaryCurrency}
          chartType={chartType}
          controlsPortalId={controlsPortalId}
          activeCampaignTotal={activeCampaignTotal}
          isRecalculating={isRecalculating}
          updateIsRecalculating={updateIsRecalculating}
          isGeneratingEstimates={isGeneratingEstimates}
          depthUnit={depthUnit}
        />
      )}
      {showTable && (
        <SummaryTable
          campaign={activeCampaign}
          probability={probability}
          chartType={chartType}
          dataEntity={dataEntity}
          primaryCurrency={primaryCurrency}
          activeCampaignTotal={activeCampaignTotal}
          depthUnit={depthUnit}
        />
      )}
    </>
  );
};

const mapStateToProps = ({ entities }) => {
  const { isRecalculating, isGeneratingEstimates } = entities.campaigns;

  return {
    depthUnit: selectUnitByQuantity(entities, QuantitiesTypes.Depth),
    isRecalculating,
    isGeneratingEstimates,
  };
};

const Container = withErrorBoundary(connect(mapStateToProps)(Summary), {
  style: { position: 'absolute', top: '50px' },
});

export { Container as Summary };
